"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, ShieldCheck, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
const fallbackNavItems = [{
  id: "f-1",
  label: "New Mechanical Bulls",
  href: "/NewMechanicalBullsPage"
}, {
  id: "f-2",
  label: "Used Mechanical Bulls",
  href: "/UsedMechanicalBullsPage"
}, {
  id: "f-3",
  label: "Parts & Accessories",
  href: "/MechanicalBullPartsAndAccessoriesPage"
}, {
  id: "f-4",
  label: "Price Guide",
  href: "/MechanicalBullPriceGuidePage"
}, {
  id: "f-5",
  label: "Buying Guide",
  href: "/MechanicalBullBuyingGuidePage"
}, {
  id: "f-6",
  label: "Setup, Safety & Maintenance",
  href: "/SetupSafetyAndMaintenancePage"
}, {
  id: "f-7",
  label: "Shipping & Ordering",
  href: "/ShippingWarrantyAndOrderingPage"
}];
function RecursiveMenuItems({
  items,
  level = 0
}: {
  items: MenuItem[];
  level?: number;
}) {
  return <ul className={level === 0 ? "space-y-2.5" : "pl-3 mt-1.5 space-y-1.5 border-l border-[#2A2A2A]"}>
      {items.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      return <li key={item.id} data-index={i} className="text-sm">
            <Link to={item.href || "#"} className="inline-flex items-center gap-1.5 text-[#BBBBBB] hover:text-[#C95716] transition-colors font-mono uppercase text-xs tracking-wider">
              <span>{item.label}</span>
              {hasChildren && <span className="text-[10px] text-primary font-bold">+</span>}
            </Link>
            {hasChildren && item.children && <RecursiveMenuItems items={item.children} level={level + 1} />}
          </li>;
    })}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2">
        <div className="h-4 w-32 bg-[#222222] animate-pulse rounded-none" />
        <div className="h-4 w-40 bg-[#222222] animate-pulse rounded-none" />
        <div className="h-4 w-28 bg-[#222222] animate-pulse rounded-none" />
      </div>;
  }
  const items = menuItems && menuItems.length > 0 ? menuItems : fallbackNavItems;
  return <RecursiveMenuItems items={items} />;
}
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="footer" data-nav="dark" className="bg-[#0A0A0A] text-white border-t border-[#171717]">
      <div className="w-full py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
            {/* Column 1: Brand & Equipment Scope */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3
          }} className="lg:col-span-4 flex flex-col space-y-5">
              <div className="inline-block">
                <Link to="/">
                  <WvcLogo className="h-12 w-auto" />
                </Link>
              </div>

              <p className="text-sm leading-relaxed text-[#BBBBBB] max-w-sm">
                Mechanical Bull Shop offers new and used mechanical bulls, parts and accessories with visible pricing, detailed product information and direct buyer support.
              </p>

              <div className="p-3 border border-[#2A2A2A] bg-[#141414] max-w-sm">
                <div className="flex items-center gap-2 text-white font-mono text-xs uppercase tracking-wider mb-1">
                  <ShieldCheck className="size-4 text-primary shrink-0" />
                </div>
                <p className="text-xs text-[#BBBBBB] leading-normal">
                  Compare equipment, product information, buying guides, setup considerations and available replacement parts.
                </p>
              </div>
            </motion.div>

            {/* Column 2: Navigation & Technical Catalog Links */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: shouldReduceMotion ? 0 : 0.05
          }} className="lg:col-span-4 flex flex-col space-y-4">
              <div className="border-b border-[#2A2A2A] pb-2">
                <h2 className="font-serif uppercase text-sm font-semibold tracking-wider text-white">
                  Equipment Catalog & Guides
                </h2>
              </div>
              <MenuProvider menu_id="23" className="w-full">
                <FooterMenuConsumer />
              </MenuProvider>
            </motion.div>

            {/* Column 3: Contact & Operating Hours */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: shouldReduceMotion ? 0 : 0.1
          }} className="lg:col-span-4 flex flex-col space-y-5">
              <div className="border-b border-[#2A2A2A] pb-2">
                <h2 className="font-serif uppercase text-sm font-semibold tracking-wider text-white">
                  Technical Sales & Support
                </h2>
              </div>

              <div className="space-y-3.5 text-sm">
                <div className="flex items-start gap-3">
                  <Mail className="size-4 text-primary shrink-0 mt-0.5" />
                  <div>
                    <span className="block text-xs font-mono uppercase text-[#BBBBBB] tracking-wider">
                      Orders & Specs
                    </span>
                    <a href="mailto:sales@mechanicalbullshop.com" className="text-white hover:text-[#C95716] transition-colors text-sm break-all">
                      sales@mechanicalbullshop.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Link to="/RequestQuotePage" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3.5 py-2 border border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-colors">
                  <span>Request Machine Quote</span>
                  <ArrowUpRight className="size-3.5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Separator className="bg-[#2A2A2A]" />

      {/* Baseline Copyright & Compliance */}
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-5">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-[#BBBBBB]">
          <p className="text-center lg:text-left">
            {"©"} {new Date().getFullYear()} Mechanical Bull Shop. Commercial equipment sales only. All rights reserved.
          </p>

          <div className="flex flex-wrap items-center justify-center lg:justify-end gap-x-3 sm:gap-x-4 gap-y-2 text-xs font-mono uppercase tracking-wider">
            <Link to="/ShippingWarrantyAndOrderingPage" className="hover:text-[#C95716] transition-colors">
              Warranty & Terms
            </Link>
            <span>/</span>
            <Link to="/SetupSafetyAndMaintenancePage" className="hover:text-[#C95716] transition-colors">
              Safety Guidance
            </Link>
            <span>/</span>
            <Link to="/ContactPage" className="hover:text-[#C95716] transition-colors">
              Support
            </Link>
            <span>/</span>
            <div className="inline-flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1">
              <span className="text-[#BBBBBB]">Policies:</span>
              <Link to="/PrivacyPolicyPage" className="hover:text-[#C95716] transition-colors">
                Privacy Policy
              </Link>
              <span>/</span>
              <Link to="/ShippingWarrantyAndOrderingPage" className="hover:text-[#C95716] transition-colors">
                Shipping Info
              </Link>
              <span>/</span>
              <Link to="#" className="hover:text-[#C95716] transition-colors">
                Refund & Returns
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}